import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { CalendarIcon, Users, Clock, Check } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { restaurantInfo } from "../../mock";

const timeSlots = [
  "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "6:00 PM",
  "6:30 PM", "7:00 PM", "7:30 PM", "8:00 PM", "8:30 PM", "9:00 PM", "9:30 PM"
];

const initialForm = { name: "", email: "", phone: "", guests: "2", time: "", notes: "" };

function validate(form, date) {
  if (!form.name || !form.email || !form.phone || !date || !form.time) {
    return "Please fill in all required fields";
  }
  return null;
}

function persistReservation(payload) {
  const existing = JSON.parse(localStorage.getItem("sea_spice_reservations") || "[]");
  localStorage.setItem("sea_spice_reservations", JSON.stringify([...existing, payload]));
}

export function ReservationInfoPanel() {
  return (
    <div className="lg:col-span-2">
      <span className="divider-ornament">Plan Your Visit</span>
      <h2 className="font-display text-sea-deep text-3xl md:text-4xl mt-4 mb-5 leading-tight">
        Reserve your seat by the coast
      </h2>
      <p className="text-sea-mid/80 leading-relaxed mb-7">
        Book online for parties of up to 8 guests. For larger groups, private dining, or special requests, please call us directly.
      </p>

      <div className="space-y-5 border-t border-sea-deep/10 pt-7">
        <div>
          <div className="text-spice-gold text-[10px] tracking-[0.3em] uppercase mb-1">Phone</div>
          <a href={`tel:${restaurantInfo.phone}`} className="font-display text-sea-deep text-xl hover:text-spice-gold">
            {restaurantInfo.phoneDisplay}
          </a>
        </div>
        <div>
          <div className="text-spice-gold text-[10px] tracking-[0.3em] uppercase mb-1">Address</div>
          <div className="text-sea-deep">{restaurantInfo.address.line1}<br />{restaurantInfo.address.line2}</div>
        </div>
        <div>
          <div className="text-spice-gold text-[10px] tracking-[0.3em] uppercase mb-1">Hours</div>
          <div className="text-sea-deep text-sm leading-relaxed">
            Mon–Sun ·  6:30 AM – 11:30 PM<br />
            <span className="text-sea-mid/60">Open Daily</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ReservationConfirmation({ form, date, onReset }) {
  return (
    <section className="min-h-screen pt-32 pb-20 bg-cream flex items-center">
      <div className="max-w-xl mx-auto text-center px-5">
        <div className="w-20 h-20 rounded-full bg-spice-gold flex items-center justify-center mx-auto mb-7">
          <Check size={36} className="text-[#0d2929]" strokeWidth={3} />
        </div>
        <span className="divider-ornament">Reservation Confirmed</span>
        <h1 className="font-display text-sea-deep text-4xl md:text-5xl mt-5 mb-5">
          Thank you, {form.name.split(" ")[0]}!
        </h1>
        <p className="text-sea-mid/80 mb-3">
          We&apos;ve received your reservation for <strong className="text-sea-deep">{form.guests} guest{form.guests !== "1" && "s"}</strong> on <strong className="text-sea-deep">{format(date, "PPP")}</strong> at <strong className="text-sea-deep">{form.time}</strong>.
        </p>
        <p className="text-sea-mid/75 text-sm mb-8">
          A confirmation will be sent to <strong>{form.email}</strong>. For any changes, please call us at {restaurantInfo.phoneDisplay}.
        </p>
        <Button onClick={onReset} className="bg-sea-deep hover:bg-[hsl(184_60%_10%)] text-white rounded-none px-8 h-12">
          Make Another Reservation
        </Button>
      </div>
    </section>
  );
}

export function ReservationForm({ onSuccess }) {
  const [form, setForm] = useState(initialForm);
  const [date, setDate] = useState();

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = (e) => {
    e.preventDefault();
    const error = validate(form, date);
    if (error) {
      toast.error(error);
      return;
    }
    persistReservation({ ...form, date: format(date, "PPP"), createdAt: new Date().toISOString() });
    toast.success("Reservation request received!");
    onSuccess(form, date);
  };

  return (
    <form onSubmit={submit} className="lg:col-span-3 bg-white p-7 lg:p-10 border border-sea-deep/10 space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <Label htmlFor="name" className="text-sea-deep text-sm font-medium">Full Name *</Label>
          <Input id="name" value={form.name} onChange={(e) => update("name", e.target.value)} className="mt-2 h-12 rounded-none border-sea-deep/15 focus-visible:ring-spice-gold focus-visible:border-spice-gold" placeholder="Jane Doe" />
        </div>
        <div>
          <Label htmlFor="phone" className="text-sea-deep text-sm font-medium">Phone *</Label>
          <Input id="phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-2 h-12 rounded-none border-sea-deep/15 focus-visible:ring-spice-gold focus-visible:border-spice-gold" placeholder="0412 345 678" />
        </div>
      </div>
      <div>
        <Label htmlFor="email" className="text-sea-deep text-sm font-medium">Email *</Label>
        <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-2 h-12 rounded-none border-sea-deep/15 focus-visible:ring-spice-gold focus-visible:border-spice-gold" placeholder="you@email.com" />
      </div>
      <div className="grid sm:grid-cols-3 gap-5">
        <div>
          <Label className="text-sea-deep text-sm font-medium">Date *</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="mt-2 w-full h-12 justify-start rounded-none border-sea-deep/15 font-normal hover:bg-cream">
                <CalendarIcon size={16} className="mr-2 text-spice-gold" />
                {date ? format(date, "PP") : <span className="text-sea-mid/50">Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={date} onSelect={setDate} initialFocus disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} />
            </PopoverContent>
          </Popover>
        </div>
        <div>
          <Label className="text-sea-deep text-sm font-medium">Time *</Label>
          <Select value={form.time} onValueChange={(v) => update("time", v)}>
            <SelectTrigger className="mt-2 h-12 rounded-none border-sea-deep/15">
              <Clock size={14} className="mr-1 text-spice-gold" />
              <SelectValue placeholder="Select time" />
            </SelectTrigger>
            <SelectContent>
              {timeSlots.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sea-deep text-sm font-medium">Guests *</Label>
          <Select value={form.guests} onValueChange={(v) => update("guests", v)}>
            <SelectTrigger className="mt-2 h-12 rounded-none border-sea-deep/15">
              <Users size={14} className="mr-1 text-spice-gold" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1,2,3,4,5,6,7,8].map((n) => <SelectItem key={n} value={String(n)}>{n} {n === 1 ? "guest" : "guests"}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label htmlFor="notes" className="text-sea-deep text-sm font-medium">Special Requests</Label>
        <Textarea id="notes" value={form.notes} onChange={(e) => update("notes", e.target.value)} className="mt-2 rounded-none border-sea-deep/15 focus-visible:ring-spice-gold focus-visible:border-spice-gold min-h-[100px]" placeholder="Allergies, occasions, seating preferences..." />
      </div>
      <Button type="submit" className="w-full bg-spice-gold hover:bg-[hsl(38_75%_46%)] text-[#0d2929] font-semibold rounded-none h-14 tracking-wide text-sm">
        Confirm Reservation
      </Button>
      <p className="text-xs text-sea-mid/60 text-center">
        By booking, you agree to our cancellation policy. We&apos;ll send a confirmation to your email.
      </p>
    </form>
  );
}
